import { useDebouncedValue, useLocalStorage } from "@mantine/hooks"
import {
  Circle,
  DotsThreeVertical,
  MagnifyingGlass,
  User,
} from "@phosphor-icons/react"
import {
  Avatar,
  InputGroup,
  Menu,
  MenuItem,
  OverflowMenu,
  Popover,
  SegmentedControl,
  Tag,
  Toast,
  Tooltip,
} from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { siteConfig } from "~/constants/config"
import FilterTypes from "~/constants/FilterTypes"
import { PRODUCTION_TRADE_STATUS } from "~/constants/ProductionTradeStatus"
import {
  Order_By,
  useAuthenticatedUserQuery,
  useProductionTradesQuery,
  useShiftProductionToStorageMutation,
  useUpdateProductionStatusMutation,
} from "~/generated/graphql"
import { ProductionFilterType } from "~/pages/d/production-planning"
import ProductionFilter from "~/pages/d/production-planning/ProductionFilters"

export const ProductionFinal = () => {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()

  const search = params.get("search")
  const page = Number(params.get("page")) || 1
  const updatedAt = params.get("updatedAt")

  const [{ data: aData }] = useAuthenticatedUserQuery()

  const currentDeptCodename = aData?.me?.agent?.current_department?.codename
  const [department] = useLocalStorage({ key: "department" })

  const [debouncedSearch] = useDebouncedValue(
    search?.trim(),
    siteConfig.debounce
  )
  const [filter, setFilter] = useState<ProductionFilterType>(() => {
    const savedFilter = localStorage.getItem(FilterTypes.productionFinalFilter)
    return savedFilter ? JSON.parse(savedFilter) : {}
  })

  useEffect(() => {
    localStorage.setItem(
      FilterTypes.productionFinalFilter,
      JSON.stringify(filter)
    )
  }, [filter])

  const [, updateProductionTradeStatus] = useUpdateProductionStatusMutation()
  const [, shiftToStorage] = useShiftProductionToStorageMutation()

  const [{ data, fetching }, refetch] = useProductionTradesQuery({
    variables: {
      limit: siteConfig.pageSize,
      offset: (page - 1) * siteConfig.pageSize,
      orderBy: { created_at: Order_By.DescNullsLast },
      where: {
        ...(debouncedSearch
          ? Number(debouncedSearch)
            ? { id: { _eq: Number(debouncedSearch) } }
            : undefined
          : undefined),
        department: {
          reference_id: {
            _eq: (department && String(department)) || currentDeptCodename,
          },
        },
        buyer: filter?.sb_buyer?.codename
          ? {
              reference_id: {
                _eq: filter?.sb_buyer?.codename,
              },
            }
          : undefined,
        kind: {
          _eq: "final",
        },
        mode: filter?.oth_mode
          ? {
              _eq: filter?.oth_mode,
            }
          : undefined,
        status: filter?.oth_status?.id
          ? {
              _eq: filter?.oth_status?.id,
            }
          : undefined,
        created_at: filter?.oth_created_at
          ? {
              _gte: dayjs(filter?.oth_created_at).startOf("day").toDate(),
              _lte: dayjs(filter?.oth_created_at).endOf("day").toDate(),
            }
          : undefined,
        start_datetime: filter?.oth_production_start
          ? {
              _gte: dayjs(filter?.oth_production_start).startOf("day").toDate(),
              _lte: dayjs(filter?.oth_production_start).endOf("day").toDate(),
            }
          : undefined,
        items:
          filter?.com_commodity?.codename || filter?.com_variety?.codename
            ? {
                commodity: filter?.com_commodity?.codename
                  ? {
                      reference_id: {
                        _eq: filter?.com_commodity?.codename,
                      },
                    }
                  : undefined,
                variety: filter?.com_variety?.codename
                  ? {
                      reference_id: {
                        _eq: filter?.com_variety?.codename,
                      },
                    }
                  : undefined,
                kind: {
                  _eq: "output",
                },
              }
            : undefined,
      },
    },
    requestPolicy: "cache-and-network",
  })
  const totalTrades = data?.production_trade_aggregate?.aggregate?.count || 0
  const COLUMNS = useMemo(
    () => [
      {
        Header: "Created On",
        accessor: "created_at",
        Cell({ value }: any) {
          return (
            <Cell
              title={dayjs(value).format("DD MMM 'YY")}
              subtitle={dayjs(value).format("h:mm a")}
            />
          )
        },
      },
      {
        Header: "Pavti No.",
        accessor: "id",
        Cell({ value }: any) {
          return (
            <Cell
              title={
                <Link to={`/d/production-planning/${value}`}>
                  {`Pavti #${value}`}
                </Link>
              }
            />
          )
        },
      },
      {
        Header: "Prod. Plan Patti Ref.",
        accessor: "parent_trade_id",
        Cell({ value }: any) {
          if (!value) return "--"
          return (
            <Cell
              title={
                <Tag
                  onClick={() => navigate(`/d/production-planning/${value}`)}
                  className="!rounded-sm !bg-[#E8625F] !text-white !border-none"
                >
                  {`#${value}`}
                </Tag>
              }
              titleClassName=""
            />
          )
        },
      },
      {
        Header: "Ref Pattis (STP)",
        accessor: "ref_storage_patti",
        Cell({ value }: any) {
          if (!value?.length) return "--"
          return (
            <div className="flex gap-1">
              <OverflowMenu
                items={value?.map((item: any) => ({
                  id: item?.storage_picked?.[0]?.trade_id,
                  label: item?.storage_picked?.[0]?.trade_id,
                }))}
                getTitle={item => `#${item?.id}`}
                visibleItems={2}
                onClick={item => {
                  navigate(`/d/trade-requests/${item?.id}`)
                }}
                getVisibleProps={() => {
                  return {
                    className:
                      "!rounded-sm bg-brown-400 !text-white !border-none",
                  }
                }}
              />
            </div>
          )
        },
      },
      {
        Header: "Linked Order Patti",
        accessor: "order_trade_refs",
        Cell({ value }: any) {
          if (!value?.length) return "--"
          return (
            <div className="flex gap-1">
              <OverflowMenu
                items={value?.map((item: any) => ({
                  id: item?.trade_id,
                  label: item?.trade_id,
                }))}
                onClick={item => {
                  navigate(`/d/trade-requests/${item?.id}`)
                }}
                visibleItems={2}
                getTitle={item => `#${item?.id}`}
                getVisibleProps={() => {
                  return {
                    className:
                      "!rounded-sm bg-blue-600 !text-white !border-none",
                  }
                }}
              />
            </div>
          )
        },
      },
      {
        Header: "Storage Pattis",
        accessor: "associated_trades",
        Cell({ value }: any) {
          if (!value?.length) return "--"
          return (
            <div className="flex gap-1">
              <OverflowMenu
                items={value}
                getTitle={item => `#${item?.id}`}
                visibleItems={2}
                onClick={item => {
                  navigate(`/d/trade-requests/${item?.id}`)
                }}
                getVisibleProps={() => {
                  return {
                    className:
                      "!rounded-sm bg-brown-400 !text-white !border-none",
                  }
                }}
              />
            </div>
          )
        },
      },
      {
        Header: "Buyer",
        accessor: "buyer.user.0",
        Cell({ value }: any) {
          if (!value) return "--"
          return (
            <Cell
              title={value?.full_name}
              subtitle={
                <Tag
                  intent="info"
                  variant="minimal"
                  className="!rounded-xl"
                  leftIcon={<User weight="fill" />}
                >
                  {value?.external_reference_id}
                </Tag>
              }
            />
          )
        },
      },
      {
        Header: "Material",
        accessor: "items.0",
        Cell({ value }: any) {
          const commodity = value?.commodity
          const variety = value?.variety
          return <Cell title={commodity?.name} subtitle={variety?.name} />
        },
      },
      {
        Header: "Grade",
        accessor: "items.0.grade",
        Cell({ value }: any) {
          return (
            <Cell
              title={
                <Tag intent="info" variant="minimal" className="!rounded-xl">
                  {value?.name}
                </Tag>
              }
            />
          )
        },
      },
      {
        Header: "Qty",
        accessor: "items.0.required_quantity",
        Cell({ value }: any) {
          return <Cell title={value} titleClassName="!text-brown-400" />
        },
      },
      {
        Header: "Rate",
        accessor: "items.0.cost_per_unit",
        Cell({ value, row: { original } }: any) {
          const totalAmount = original?.items?.[0]?.total_cost
          return (
            <Cell
              titleClassName="text-brown-400"
              title={`${inr(value)}/kg`}
              subtitle={inr(totalAmount)}
            />
          )
        },
      },
      {
        Header: "Production Start",
        accessor: "start_datetime",
        Cell({ value }: any) {
          return (
            <Cell
              title={dayjs(value).format("DD MMM 'YY")}
              subtitle={dayjs(value).format("h:mm A")}
            />
          )
        },
      },
      {
        Header: "Placed By",
        accessor: "created_by",
        Cell({ value }) {
          return (
            <Tooltip
              content={
                <div>
                  <p>{value?.full_name}</p>
                  <p>{dayjs(value?.last_login).format("DD MMM 'YY hh:mm A")}</p>
                </div>
              }
            >
              <Avatar name={value?.full_name} />
            </Tooltip>
          )
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell({ value }: any) {
          if (!value) return "--"
          const status = PRODUCTION_TRADE_STATUS[value.toUpperCase()]
          return (
            <Cell
              title={
                <div className="flex items-center gap-1">
                  <Circle weight="fill" />
                  {status?.label}
                </div>
              }
              titleClassName={classNames(status?.color)}
            />
          )
        },
      },
      {
        Header: "",
        accessor: "actions",
        Cell({ row: { original } }) {
          const [params, setParams] = useSearchParams()
          const navigate = useNavigate()
          const [isShown, setShown] = useState(false)

          const canCancel =
            original.status === PRODUCTION_TRADE_STATUS.SENT_FOR_APPROVAL?.id
          const canShift =
            original.status === PRODUCTION_TRADE_STATUS.APPROVED?.id

          return (
            <Popover
              onShow={() => setShown(true)}
              minimal
              arrow={false}
              className="p-0 max-h-[500px] shadow"
              trigger="click"
              interactive
              animation="fade"
              theme="light-border"
              placement="bottom"
              content={
                <Menu className="max-h-fit">
                  <MenuItem
                    onClick={() => {
                      navigate(`/d/production-planning/${original.id}`)
                    }}
                  >
                    View Details
                  </MenuItem>
                  <hr />
                  <div className="text-gray-800 font-600 p-2 underline">
                    Other
                  </div>
                  <MenuItem
                    disabled={!canShift}
                    onClick={async () => {
                      const { error, data } = await shiftToStorage({
                        tradeId: original.id,
                      })
                      params.set("updatedAt", Date.now().toString())
                      setParams(params)
                      if (error) {
                        toast.custom(
                          <Toast title={error.message} intent="danger" />
                        )
                      } else {
                        toast.custom(
                          <Toast
                            title={`Storage pattis ${data?.shift_production_trade_to_storage?.production_trade?.associated_trades
                              ?.map(trade => trade.id)
                              .join(", ")} created`}
                          />
                        )
                      }
                    }}
                  >
                    Shift to Storage
                  </MenuItem>
                  <hr />
                  <div className="text-gray-800 font-600 p-2 underline">
                    Other
                  </div>
                  <MenuItem
                    disabled={!canCancel}
                    onClick={async () => {
                      const { error } = await updateProductionTradeStatus({
                        status: PRODUCTION_TRADE_STATUS.CANCELLED?.id,
                        trade_id: original.id,
                      })
                      params.set("updatedAt", Date.now().toString())
                      setParams(params)
                      if (error) {
                        toast.custom(
                          <Toast title={error.message} intent="danger" />
                        )
                      } else {
                        toast.custom(
                          <Toast title="Production Planning Cancelled" />
                        )
                      }
                      setShown(false)
                    }}
                    className="text-red"
                  >
                    Cancel Production
                  </MenuItem>
                </Menu>
              }
            >
              <button className="hover:bg-gray-300 rounded">
                <DotsThreeVertical size={20} />
              </button>
            </Popover>
          )
        },
      },
    ],
    []
  )

  useEffect(() => {
    if (updatedAt) {
      refetch({
        requestPolicy: "network-only",
      })
      params.delete("updatedAt")
      setParams(params)
    }
  }, [updatedAt])

  return (
    <>
      <header className="flex flex-col md:grid md:grid-cols-3 items-start md:items-center justify-between py-2 pl-2 pr-1 border-b-1 border-b-gray-300 gap-3">
        <SegmentedControl
          value={"final"}
          onChange={value => {
            if (value === "planning") {
              navigate("/d/production-planning")
            }
          }}
          className="w-full md:w-fit overflow-x-auto"
          data={[
            {
              label: "Production Planning",
              value: "planning",
            },
            {
              label: "Production Patti",
              value: "final",
            },
          ].filter(b => b)}
        />
        <div className="w-full md:flex-1 md:max-w-md flex items-center gap-3">
          <InputGroup
            className={"w-full"}
            inputProps={{
              value: search || "",
              placeholder: "Search by Pavti no.",
              onChange(e) {
                params.set("search", e.target.value)
                params.delete("page")
                setParams(params)
              },
            }}
            leftElement={<MagnifyingGlass />}
          />
          <ProductionFilter filter={filter} setFilter={setFilter} />
        </div>
        <div className="flex gap-3 justify-end w-full md:w-auto"></div>
      </header>

      <TableView
        columns={COLUMNS}
        data={data?.production_trade || []}
        relative={false}
        className="!h-[calc(100vh-64px-57px)]"
        paginationProps={{
          total: Math.ceil(totalTrades / siteConfig.pageSize),
          page: page,
          onChange(page) {
            params.set("page", page.toString())
            params.set("search", "")
            setParams(params)
          },
        }}
        isLoading={fetching}
        meta={<>{totalTrades} trade requests</>}
        tableClassName="!h-full bg-white"
      />
    </>
  )
}

export default ProductionFinal
